/**
 * Formatage monétaire.
 *
 * L'app affichait « 4218.50 € » : point décimal anglo-saxon et aucun
 * séparateur de milliers. Sur un produit financier francophone, c'est le
 * premier détail qui trahit l'amateurisme. Tout passe désormais par
 * Intl.NumberFormat en fr-FR, qui rend « 4 218,50 € ».
 */

const SYMBOLS = {
  EUR: '€', USD: '$', GBP: '£', JPY: '¥', CNY: '¥',
  CHF: 'CHF', CAD: 'C$', AUD: 'A$', NZD: 'NZ$', SEK: 'kr',
  NOK: 'kr', DKK: 'kr', PLN: 'zł', CZK: 'Kč', HUF: 'Ft',
  RON: 'lei', RUB: '₽', TRY: '₺', BRL: 'R$', MXN: '$',
  ARS: '$', CLP: '$', COP: '$', PEN: 'S/', KRW: '₩',
  INR: '₹', SGD: 'S$', HKD: 'HK$', TWD: 'NT$', THB: '฿',
  MYR: 'RM', IDR: 'Rp', PHP: '₱', VND: '₫', ZAR: 'R',
  EGP: 'E£', NGN: '₦', KES: 'KSh', GHS: 'GH₵', MAD: 'DH',
  TND: 'DT', XOF: 'CFA', AED: 'د.إ', SAR: '﷼', ILS: '₪',
}

export function getCurrencySymbol(devise) {
  return SYMBOLS[devise] || devise || '€'
}

// Instancier un NumberFormat coûte cher : on les garde en cache par devise.
const cache = new Map()

function numberFormatter(devise, options) {
  const key = `${devise}|${options.minimumFractionDigits}|${options.maximumFractionDigits}`
  if (!cache.has(key)) {
    cache.set(
      key,
      new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: options.minimumFractionDigits,
        maximumFractionDigits: options.maximumFractionDigits,
      })
    )
  }
  return cache.get(key)
}

function toNumber(amount) {
  if (amount === null || amount === undefined || amount === '') return null
  const n = typeof amount === 'number' ? amount : parseFloat(amount)
  return Number.isFinite(n) ? n : null
}

const SCALES = [
  { limit: 1e15, suffix: 'Q' },
  { limit: 1e12, suffix: 'T' },
  { limit: 1e9, suffix: 'Md' },
  { limit: 1e6, suffix: 'M' },
]

/**
 * Rend un montant en chaîne française complète : « -1 234,50 € ».
 * C'est la forme de référence, utilisée partout où l'on a besoin d'un texte
 * (titres de confirmation, exports, aria-label).
 */
export function formatMoneySimple(amount, devise = 'EUR') {
  const n = toNumber(amount)
  const symbol = getCurrencySymbol(devise)
  if (n === null) return `0,00 ${symbol}`
  // L'espace insécable avant le symbole est la convention typographique
  // française : le montant et sa devise ne doivent jamais être séparés
  // par un retour à la ligne.
  return `${numberFormatter(devise, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)} ${symbol}`
}

/**
 * Version abrégée pour les très grands nombres : « 2,4 M€ ».
 * En dessous d'un million, identique à formatMoneySimple.
 */
export function formatMoneyCompact(amount, devise = 'EUR') {
  const n = toNumber(amount)
  const symbol = getCurrencySymbol(devise)
  if (n === null) return `0,00 ${symbol}`

  const abs = Math.abs(n)
  const scale = SCALES.find((s) => abs >= s.limit)
  if (!scale) return formatMoneySimple(n, devise)

  const value = n / scale.limit
  const formatted = numberFormatter(devise, {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.abs(value) < 100 ? 2 : 1,
  }).format(value)
  return `${formatted} ${scale.suffix}${symbol}`
}

/**
 * Composant d'affichage d'un montant.
 *
 * Trois décisions y sont centralisées :
 *  — chiffres tabulaires, pour que les colonnes s'alignent et qu'un montant
 *    qui change ne fasse pas sauter la ligne ;
 *  — abréviation au-delà du million, avec la valeur exacte conservée dans
 *    le title et pour les lecteurs d'écran ;
 *  — couleur sémantique optionnelle, jamais appliquée par défaut : un solde
 *    n'est pas « bon » parce qu'il est positif.
 *
 * @param {number}  value    montant
 * @param {string}  devise   code ISO
 * @param {boolean} signed   force le « + » devant les montants positifs
 * @param {'auto'|'none'|'positive'|'negative'} tone  couleur sémantique
 * @param {boolean} compact  abrège au-delà du million
 */
export function Amount({
  value,
  devise = 'EUR',
  signed = false,
  tone = 'none',
  compact = true,
  className = '',
  ...rest
}) {
  const n = toNumber(value) ?? 0
  const exact = formatMoneySimple(n, devise)
  const display = compact ? formatMoneyCompact(n, devise) : exact
  const abbreviated = display !== exact

  const sign = signed && n > 0 ? '+' : ''

  const toneClass =
    tone === 'positive'
      ? 'text-positive'
      : tone === 'negative'
        ? 'text-negative'
        : tone === 'auto'
          ? n < 0
            ? 'text-negative'
            : 'text-positive'
          : ''

  return (
    <span
      data-numeric=""
      className={`whitespace-nowrap ${toneClass} ${className}`}
      title={abbreviated ? exact : undefined}
      aria-label={abbreviated ? `${sign}${exact}` : undefined}
      {...rest}
    >
      {sign}
      {display}
    </span>
  )
}

/**
 * Ancienne API, conservée pour ne pas casser les écrans pas encore repris.
 * Nouveau code : préférer <Amount />.
 */
export function formatMoney(amount, devise = 'EUR') {
  return <Amount value={amount} devise={devise} />
}

export default Amount
